import React, { Suspense } from 'react';
import { createRouter, RouterProvider, createRoute, createRootRoute } from '@tanstack/react-router';
import './index.css';
import { PostHogProvider } from 'posthog-js/react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { persistStore } from 'redux-persist';
// Lazy-load large pages and layouts to reduce initial bundle size on full reload
const CourseLanding = React.lazy(() => import('./pages/courses/courseLanding.jsx'));
const ExploreCoursesPage = React.lazy(() => import('./pages/courses/exploreCourses.jsx'));
const LessonLayout = React.lazy(() => import('./layout/LessonLayout'));
const LessonLandingPage = React.lazy(() => import('./pages/lessons/LandingPage.jsx'));
const ProjectLanding = React.lazy(() => import('./pages/lessons/ProjectLanding.jsx'));
const Dashboard = React.lazy(() => import('./layout/dashboard.jsx'));
const LeaderboardPage = React.lazy(() => import('./pages/leaderboards/LeaderboardPage.jsx'));
const LiveClassesPage = React.lazy(() => import('./pages/live-classes/LiveClassesPage.jsx'));
const ProjectFeedback = React.lazy(() => import('./pages/project-feedback/projectFeedback.jsx'));
const ProgressDetailsPage = React.lazy(() => import('./pages/pathways/ProgressDetailsPage.jsx'));
const ChatWithoutSidebar = React.lazy(() => import('./pages/ChatPage/ChatWithoutSidebar.jsx'));
const BillingPage = React.lazy(() => import('./pages/billing/BillingPage.jsx'));
const ProfilePage = React.lazy(() => import('./pages/profile/ProfilePage.jsx'));
import store from './store/slices/store.js';
import FAQPage from './pages/faq/FAQPage.jsx';
import ComingSoon from './pages/error-pages/comingSoon.jsx';
import Unauthorized from './pages/error-pages/Unauthorized.jsx';
import NotFound from './pages/error-pages/NotFound.jsx';
import ClerkProviderWithRoutes from './auth/ClerkProviderWithRoutes.jsx';
import { AuthenticationPage } from './pages/accounts-pages/authenticationPage.jsx';
import TrackProgress from './pages/pathways/trackProgress.jsx';
import ViewRecording from './pages/live-classes/viewRecording.jsx';
import PathwayPage from './pages/pathways/pathwayPage.jsx';
import MorePage from './layout/MorePage.jsx';
import NotificationsPage from './pages/notifications/NotificationsPage.jsx';
import { usePostHogIdentification } from './hooks/usePostHogIdentification.js';
import { Toaster } from './components/ui/sonner.jsx';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/queryClient';
import TopLoadingBar from './components/ui/TopLoadingBar';
import DashboardWrapper from './layout/DashboardWrapper';

import { startLoading, stopLoading } from './components/ui/topLoadingController';
import { ThemeProvider } from './contexts/ThemeContext.jsx';
import RootPage from './pages/coverPage/RootPage.jsx';
import CoursesCoverPage from './pages/coverPage/CoursesCoverPage.jsx';
import CoverPageCourseLanding from './pages/coverPage/CoverPageCourseLanding.jsx';
import AchievementsPage from './pages/achievements/AchievementsPage.jsx';
import PublicRoute from './components/dashboard-layout/PublicRoute.jsx';
import Preferences from './auth/Preferences.jsx';
import PrivateRoute from './components/dashboard-layout/PrivateRoute.jsx';
import PrivateNoChrome from './components/dashboard-layout/PrivateNoChrome.jsx';
import SharedBadge from './pages/shared/SharedBadge.jsx';
import StudentIDPage from './pages/student-id/StudentID.jsx';

// Create a root route
const rootRoute = createRootRoute({
    errorComponent: NotFound,
    notFoundComponent: NotFound,
});

// Create route definitions
const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    component: RootPage,
});
const unauthorizedRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/unauthorized',
    component: Unauthorized,
});
const coverCourseRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/cover-course/$courseSlug',
    component: () => (
        <PublicRoute>
            <CoverPageCourseLanding />
        </PublicRoute>
    ),
});
const availableCoursesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/available-courses',
    component: () => (
        <PublicRoute>
            <CoursesCoverPage />
        </PublicRoute>
    ),
});
const comingSoonRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/coming-soon',
    component: ComingSoon,
});
const dashboardRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/dashboard',
    component: () => (
        <PrivateRoute>
            <Suspense fallback={<DashboardSkeleton />}>
                <Dashboard />
            </Suspense>
        </PrivateRoute>
    ),
});
const projectRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/project/$projectId',
    component: () => (
        <PrivateRoute>
            <Suspense fallback={<DashboardSkeleton />}>
                <ProjectLanding />
            </Suspense>
        </PrivateRoute>
    ),
});
const leaderboardRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/leaderboard',
    component: () => (
        <PrivateRoute>
            <Suspense fallback={<DashboardSkeleton />}>
                <LeaderboardPage />
            </Suspense>
        </PrivateRoute>
    ),
});
const courseRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/course/$courseSlug',
    component: () => (
        <PrivateRoute>
            <Suspense fallback={<TopLoadingBar />}>
                <CourseLanding />
            </Suspense>
        </PrivateRoute>
    ),
});
const lessonsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/lessons/$courseSlug',
    component: () => (
        <PrivateNoChrome>
            <Suspense fallback={<TopLoadingBar />}>
                <LessonLayout>
                    <LessonLandingPage />
                </LessonLayout>
            </Suspense>
        </PrivateNoChrome>
    ),
});
const exploreCoursesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/explore-courses',
    component: () => (
        <PrivateRoute>
            <Suspense fallback={<DashboardSkeleton />}>
                <ExploreCoursesPage />
            </Suspense>
        </PrivateRoute>
    ),
});
const achievementsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/achievements',
    component: () => (
        <PrivateRoute>
            <Suspense fallback={<DashboardSkeleton />}>
                <AchievementsPage />
            </Suspense>
        </PrivateRoute>
    ),
});
const projectFeedbackRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/project-feedback',
    component: () => (
        <PrivateRoute>
            <Suspense fallback={<DashboardSkeleton />}>
                <ProjectFeedback />
            </Suspense>
        </PrivateRoute>
    ),
});
const progressDetailsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/progress-details/$cohortId',
    component: () => (
        <PrivateRoute>
            <Suspense fallback={<DashboardSkeleton />}>
                <ProgressDetailsPage />
            </Suspense>
        </PrivateRoute>
    ),
});
const liveClassesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/live-classes',
    component: () => (
        <PrivateRoute>
            <Suspense fallback={<DashboardSkeleton />}>
                <LiveClassesPage />
            </Suspense>
        </PrivateRoute>
    ),
});

const signInRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: 'sign-in',
    component: AuthenticationPage,
});

// Catch-all for Clerk multi-step sign-in routes (factor-one, forgot-password, etc.)
const signInStepRoute = createRoute({
    getParentRoute: () => signInRoute,
    path: '$clerkStep',
    component: AuthenticationPage,
});

// Clerk SignIn sub-routes (multi-step flow)
const signInFactorOneRoute = createRoute({
    getParentRoute: () => signInRoute,
    path: 'factor-one',
    component: AuthenticationPage,
});

const signInForgotPasswordRoute = createRoute({
    getParentRoute: () => signInRoute,
    path: 'forgot-password',
    component: AuthenticationPage,
});

const signInResetPasswordRoute = createRoute({
    getParentRoute: () => signInRoute,
    path: 'reset-password',
    component: AuthenticationPage,
});

const signUpRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: 'sign-up',
    component: AuthenticationPage,
});

// Catch-all for Clerk multi-step sign-up routes (verify-email, continue, set-password, etc.)
const signUpStepRoute = createRoute({
    getParentRoute: () => signUpRoute,
    path: '$clerkStep',
    component: AuthenticationPage,
});

// Clerk SignUp sub-routes (multi-step flow)
const signUpVerifyEmailRoute = createRoute({
    getParentRoute: () => signUpRoute,
    path: 'verify-email',
    component: AuthenticationPage,
});

const signUpSetPasswordRoute = createRoute({
    getParentRoute: () => signUpRoute,
    path: 'set-password',
    component: AuthenticationPage,
});

const trackProgressRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/track-progress',
    component: () => (
        <PrivateRoute>
            <Suspense fallback={<DashboardSkeleton />}>
                <TrackProgress />
            </Suspense>
        </PrivateRoute>
    ),
});
const recordingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/recording/$recordingId',
    component: () => (
        <PrivateRoute>
            <Suspense fallback={<DashboardSkeleton />}>
                <ViewRecording />
            </Suspense>
        </PrivateRoute>
    ),
});
const pathwayRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/pathway/$pathwayId',
    component: () => (
        <PrivateRoute>
            <Suspense fallback={<DashboardSkeleton />}>
                <PathwayPage />
            </Suspense>
        </PrivateRoute>
    ),
});
const moreRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/more',
    component: () => (
        <PrivateRoute>
            <Suspense fallback={<DashboardSkeleton />}>
                <MorePage />
            </Suspense>
        </PrivateRoute>
    ),
});
const notificationsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/notifications',
    component: () => (
        <PrivateRoute>
            <Suspense fallback={<DashboardSkeleton />}>
                <NotificationsPage />
            </Suspense>
        </PrivateRoute>
    ),
});
const faqRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/faq',
    component: () => (
        <PrivateRoute>
            <Suspense fallback={<DashboardSkeleton />}>
                <FAQPage />
            </Suspense>
        </PrivateRoute>
    ),
});
const preferencesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/preferences',
    component: () => (
        <PrivateRoute>
            <Suspense fallback={<DashboardSkeleton />}>
                <Preferences />
            </Suspense>
        </PrivateRoute>
    ),
});
const studentIDRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/student-id',
    component: () => (
        <PrivateRoute>
            <Suspense fallback={<DashboardSkeleton />}>
                <StudentIDPage />
            </Suspense>
        </PrivateRoute>
    ),
});
const sharedBadgeRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/shared/badge',
    component: () => (
        <PublicRoute>
            <SharedBadge />
        </PublicRoute>
    ),
});
const billingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/billing',
    component: () => (
        <PrivateRoute>
            <Suspense fallback={<DashboardSkeleton />}>{import.meta.env.PROD ? <BillingPage /> : <ComingSoon />}</Suspense>
        </PrivateRoute>
    ),
});
const profileRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/profile',
    component: () => (
        <PrivateRoute>
            <Suspense fallback={<DashboardSkeleton />}>{import.meta.env.PROD ? <ProfilePage /> : <ComingSoon />}</Suspense>
        </PrivateRoute>
    ),
});

const chatRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/chat',
    validateSearch: search => ({
        prompt: typeof search.prompt === 'string' ? search.prompt : undefined,
    }),
    component: () => (
        <PrivateNoChrome>
            <Suspense fallback={<DashboardSkeleton />}>
                <ChatWithoutSidebar />
            </Suspense>
        </PrivateNoChrome>
    ),
});
const notFoundRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '*',
    component: NotFound,
});

// Create the route tree
const routeTree = rootRoute.addChildren([
    signInRoute.addChildren([signInStepRoute, signInFactorOneRoute, signInForgotPasswordRoute, signInResetPasswordRoute]),
    signUpRoute.addChildren([signUpStepRoute, signUpVerifyEmailRoute, signUpSetPasswordRoute]),
    indexRoute,
    unauthorizedRoute,
    coverCourseRoute,
    availableCoursesRoute,
    comingSoonRoute,
    dashboardRoute,
    projectRoute,
    leaderboardRoute,
    courseRoute,
    lessonsRoute,
    exploreCoursesRoute,
    achievementsRoute,
    projectFeedbackRoute,
    progressDetailsRoute,
    liveClassesRoute,
    trackProgressRoute,
    recordingRoute,
    pathwayRoute,
    moreRoute,
    studentIDRoute,
    faqRoute,
    notificationsRoute,
    preferencesRoute,
    sharedBadgeRoute,
    billingRoute,
    profileRoute,
    chatRoute,
    // NOTE: The catch-all route must be last.
    notFoundRoute,
]);

// Create the router instance with prefetching for fast navigation
const router = createRouter({
    routeTree,
    // Prefetch on intent (hover/touch) for instant navigation
    defaultPreload: 'intent',
    // Start prefetching immediately on hover (0ms delay)
    defaultPreloadDelay: 0,
    // Keep prefetched data fresh for 5 minutes
    defaultPreloadStaleTime: 5 * 60 * 1000,
    // Cache successful navigations for 10 minutes
    defaultGcTime: 10 * 60 * 1000,
});

// Register the router for maximum type safety
/*
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
*/

let persistor = persistStore(store);

// Component that uses the PostHog identification hook
const PostHogIdentifier = () => {
    usePostHogIdentification();
    return null;
};

// Small reusable dashboard skeleton used as Suspense fallback so the chrome
// (TopBar/Sidebar) remains visible while route content loads.
const DashboardSkeleton = () => (
    <DashboardWrapper>
        <div className="animate-pulse flex flex-col gap-5">
            <div className="h-8 bg-neutral-200 rounded w-1/3" />
            <div className="h-4 bg-neutral-200 rounded w-1/2" />
            <div className="h-48 bg-neutral-200 rounded-2xl" />
        </div>
    </DashboardWrapper>
);

const App = ({ posthogInstance }) => {
    const content = (
        <ThemeProvider>
            <ClerkProviderWithRoutes>
                <Provider store={store}>
                    <PersistGate loading={null} persistor={persistor}>
                        <QueryClientProvider client={queryClient}>
                            {/* PostHog identifier is here - it will run whenever auth state changes */}
                            {posthogInstance && <PostHogIdentifier />}
                            <TopLoadingBar />
                            <RouterProvider router={router} />
                            <ReactQueryDevtools initialIsOpen={false} />
                        </QueryClientProvider>
                        <Toaster position="top-right" closeButton={true} richColors />
                    </PersistGate>
                </Provider>
            </ClerkProviderWithRoutes>
        </ThemeProvider>
    );

    if (posthogInstance) {
        return <PostHogProvider client={posthogInstance}>{content}</PostHogProvider>;
    }

    return content;
};

export default App;

// Hook up router navigation events to the TopLoadingBar imperative API
try {
    // Use tanstack router's subscribe API to detect navigation start/finish.
    // These event names are a best-effort mapping to the router lifecycle.
    router.subscribe('onBeforeLoad', () => startLoading());
    router.subscribe('onLoad', () => stopLoading());
} catch {
    // If subscribe with named events isn't supported, attempt generic subscribe
    try {
        router.subscribe(() => {
            // Called on route change completion
            stopLoading();
        });
    } catch {
        // Silent fallback
        // console.warn('Router navigation hooks not attached', err);
    }
}
